import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { GetApp } from "@mui/icons-material";

export interface ExportCsvProps {
  source?: string;
  disabled?: boolean;
  loading?: boolean;
  enableExcel?: boolean;
  onClick: (format: "csv" | "xlsx") => void;
}

const ExportCsv: React.FC<ExportCsvProps> = ({ source, loading, disabled = false, enableExcel = false, onClick }) => {
  const useButtonStyles = makeStyles(() => ({
    label: {
      whiteSpace: "nowrap",
    },
  }));

  const buttonStyles = useButtonStyles();
  const [download, setDownload] = useState(source);
  const [activeFormat, setActiveFormat] = useState<"csv" | "xlsx" | null>(null);

  useEffect(() => {
    if (source && source !== download) {
      const anchor = document.createElement("a");

      if (anchor) {
        anchor.href = source;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      }

      setDownload(source);
    }
  }, [source, download]);

  useEffect(() => {
    if (!loading) {
      setActiveFormat(null);
    }
  }, [loading]);

  const useTooltipStyles = makeStyles(() => ({
    popper: {
      zIndex: 2000,
    },
  }));
  const tooltipStyles = useTooltipStyles();

  const handleExport = (format: "csv" | "xlsx") => {
    if (disabled || loading) return;
    setActiveFormat(format);
    onClick(format);
  };

  const renderButtons = () => (
    <Box display="flex" gap={2}>
      <Button
        classes={buttonStyles}
        variant="outlined"
        onClick={() => handleExport("csv")}
        disabled={disabled || !!loading}
        startIcon={loading && activeFormat === "csv" ? <CircularProgress size={20} thickness={6} /> : <GetApp />}
      >
        Export CSV
      </Button>
      {enableExcel && (
        <Button
          classes={buttonStyles}
          variant="outlined"
          onClick={() => handleExport("xlsx")}
          disabled={disabled || !!loading}
          startIcon={loading && activeFormat === "xlsx" ? <CircularProgress size={20} thickness={6} /> : <GetApp />}
        >
          Export Excel
        </Button>
      )}
    </Box>
  );

  if (!disabled) {
    return renderButtons();
  }

  return (
    <Tooltip title="Exports are limited to 2,000 records" arrow placement="top" classes={tooltipStyles}>
      <Box>
        {renderButtons()}
      </Box>
    </Tooltip>
  );
};

export default ExportCsv;
